// Phase 1.y · Help Centre (2026-02-24, founder-locked).
//
// Lightweight, focused help. NOT a support ticket system.
// Two personas: Homeowners · For Painters. Search filters by title + body.
// Language honours the LOCKED design principle — never "surface", "mask" or
// "mode"; always "a part of your home".
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, ArrowRight, ChevronDown } from "lucide-react";

const TOPICS = {
  homeowner: [
    {
      id: "start",
      title: "How do I start a new project?",
      summary: "Snap a photo, upload it, and start colouring within a minute.",
      body: [
        "Tap Start Visualising in the top right, or open Projects → New project.",
        "Upload a clear photo of the outside of your home, a room, or any part of your home you want to design.",
        "You can add up to 8 photos to the same project (front elevation, side, pool area, garage, hallway, and so on) so the whole property lives in one place.",
      ],
    },
    {
      id: "choose-area",
      title: "Choose an area, then choose a part of your home",
      summary: "Every project starts with a simple, no-jargon flow.",
      body: [
        "1. Choose an area (Exterior, Kitchen, Living Room, Outdoor Living…).",
        "2. Tap the part of your home you want to colour (Walls, Roof, Fascia, Gutters, Front Door…).",
        "3. Pick a colour from Recommended or search by paint name.",
        "4. Hit Apply.",
        "5. When you are happy with all your colours, hit Render Scheme to see your home painted.",
      ],
    },
    {
      id: "recommended-vs-library",
      title: "Recommended colours vs. the full 1,041-colour library",
      summary: "The Workspace shows 12 hand-picked colours for the part you are colouring. The full library lives one click away.",
      body: [
        "In the Colour Studio you'll see 12 Recommended swatches curated for the part of your home you are colouring, plus a Favourites tab.",
        "Type into the search box to search paint names or codes.",
        "For the complete browsing experience across all 1,041 ColourMatch colours, tap Browse all 1,041 colours → at the top or bottom of the Studio. You can filter by family, warmth and brightness there, and any colour you pick will come back with you to the Workspace ready to Apply.",
      ],
    },
    {
      id: "free-preview",
      title: "Free preview vs. unlocking your project",
      summary: "Design as much as you want. Pay once when you're ready to render.",
      body: [
        "You can build a full colour scheme in Free preview — apply colours to as many parts of your home as you like.",
        "When you're happy and want to see your home painted, hit Render Scheme. The button becomes Unlock to render if the project is still in Free preview.",
        "The one-time unlock is $29.99 AUD and includes unlimited renders on that project, HD downloads, and the Paint Specification PDF.",
        "After Stripe payment you're returned directly to your Workspace with every colour choice intact — you just hit Render Scheme.",
      ],
    },
    {
      id: "saved-renders",
      title: "Where are my saved renders?",
      summary: "Every render is kept as a version underneath your photo.",
      body: [
        "Every time you hit Render Scheme, a new Version tile appears in Your Saved Renders on the left of the Workspace.",
        "Click any tile to preview that version in place of the main image. Original is always first for reference.",
        "Each version tile has a Download button so you can save the render to your device as a JPG.",
      ],
    },
    {
      id: "share-painter",
      title: "Sharing your project with a painter",
      summary: "Send a painter your scheme with one link.",
      body: [
        "Open your project and tap Invite my painter in the top-right of the Workspace.",
        "You can send the invite by email or copy a share link — the painter sees your colours, your renders, and the exact paint codes they need to quote.",
        "You can also browse ColourMatch Painters from the Workspace to find approved local pros.",
      ],
    },
  ],
  painter: [
    {
      id: "pro-what",
      title: "What is ColourMatch Pro?",
      summary: "A membership that lists you in the painter directory and unlocks the painter workspace.",
      body: [
        "ColourMatch Pro is a paid membership for professional painters. It lists you in the ColourMatch Painters directory homeowners can browse from every Workspace, and unlocks the painter tools at /pro.",
        "Homeowners can invite you to their projects (colour schemes + paint codes + renders) so you can quote faster and more accurately.",
      ],
    },
    {
      id: "leads",
      title: "Where do my leads appear?",
      summary: "Painter Leads combines homeowner shares and inbound directory requests.",
      body: [
        "Open your painter workspace at /pro. The Leads screen surfaces both homeowners who have shared a project with you and inbound requests from your ColourMatch Painters directory listing.",
        "Each lead card shows the customer, their scheme, and the actions you can take next (View project, Quote, Message).",
      ],
    },
    {
      id: "accept-invite",
      title: "Accepting a homeowner invite",
      summary: "Follow the invite link and sign in to your ColourMatch Pro account.",
      body: [
        "When a homeowner invites you, you'll receive an email with a link that includes a share token.",
        "Sign in with your ColourMatch Pro account and the shared project appears in your Leads. You can view the exact paint colours, codes and renders they've saved.",
      ],
    },
    {
      id: "presentations",
      title: "Building a colour presentation for a client",
      summary: "Package your scheme into a shareable, professional proposal.",
      body: [
        "From the painter workspace, open a project or presentation and add the colours and renders you want to send.",
        "Share the presentation link with your client. When they accept, the acceptance is recorded on the presentation and you're notified.",
      ],
    },
    {
      id: "billing",
      title: "Membership billing",
      summary: "Payments handled by Stripe. Manage from the ColourMatch Pro tab.",
      body: [
        "Membership is billed via Stripe. Open /pro → membership to manage your tier, view invoices, and update payment details.",
        "If your card fails or you cancel, your public profile is removed from the ColourMatch Painters directory until reactivation.",
      ],
    },
  ],
};

const PERSONAS = [
  { key: "homeowner", label: "For homeowners" },
  { key: "painter",   label: "For painters" },
];

function matches(topic, q) {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  const hay = [topic.title, topic.summary, ...(topic.body || [])].join(" ").toLowerCase();
  return hay.includes(needle);
}

export default function HelpCentre() {
  const [persona, setPersona] = useState("homeowner");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const list = useMemo(() => TOPICS[persona].filter((t) => matches(t, q)), [persona, q]);

  return (
    <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16" data-testid="help-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">
          <HelpCircle size={12} /> Help &amp; Guides
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-brand-ink tracking-tight font-serif" data-testid="help-title">
          How can we help?
        </h1>
        <p className="text-sm text-brand-muted mt-3 max-w-lg">
          Short, plain-English answers to the questions we hear most. Search below, or browse by persona.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search help topics"
          data-testid="help-search"
          aria-label="Search help topics"
          className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-brand-border text-sm placeholder:text-brand-muted focus:outline-none focus:border-brand-teal focus:ring-4 focus:ring-brand-teal/10 transition-all"
        />
      </div>

      {/* Persona tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="help-persona-tabs">
        {PERSONAS.map((p) => (
          <button
            key={p.key}
            onClick={() => { setPersona(p.key); setOpenId(null); }}
            data-testid={`help-persona-${p.key}`}
            className={`h-9 px-4 rounded-full text-xs font-semibold transition-colors border ${
              persona === p.key
                ? "bg-brand-ink text-white border-brand-ink"
                : "bg-white text-brand-ink border-brand-border hover:border-brand-ink/40"
            }`}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-brand-muted" data-testid="help-count">
          {list.length} {list.length === 1 ? "topic" : "topics"}
        </span>
      </div>

      {/* Topic list */}
      <div className="space-y-2" data-testid="help-list">
        {list.map((t) => {
          const open = openId === t.id;
          return (
            <div key={t.id} className="bg-white rounded-2xl border border-brand-border overflow-hidden">
              <button
                onClick={() => setOpenId(open ? null : t.id)}
                data-testid={`help-topic-${t.id}`}
                aria-expanded={open}
                className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-brand-linen/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-brand-ink font-serif">{t.title}</div>
                  <div className="text-[12px] text-brand-muted mt-0.5">{t.summary}</div>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-brand-muted mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>
              {open && (
                <div className="px-5 pb-5 pt-1 space-y-2 text-sm text-brand-ink leading-relaxed" data-testid={`help-body-${t.id}`}>
                  {t.body.map((p, i) => (
                    <p key={i} className="text-brand-ink/85">{p}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="bg-white rounded-2xl border border-brand-border px-5 py-10 text-center text-sm text-brand-muted" data-testid="help-empty-state">
            No topics match &ldquo;{q}&rdquo;. Try a different search or clear the filter.
          </div>
        )}
      </div>

      {/* Footer: escalation */}
      <div className="mt-10 bg-brand-linen rounded-3xl px-6 py-5 flex items-center justify-between gap-4" data-testid="help-footer">
        <div>
          <div className="text-sm font-semibold text-brand-ink font-serif">Still stuck?</div>
          <p className="text-[12px] text-brand-muted mt-0.5">
            Send us a message — we usually reply within a business day.
          </p>
        </div>
        <Link
          to="/contact"
          data-testid="help-contact-cta"
          className="inline-flex items-center gap-1.5 bg-brand-navy text-white h-10 px-4 rounded-2xl text-xs font-semibold hover:bg-brand-navy/90 transition-colors shrink-0"
        >
          Contact support <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
